import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { ResortOverview } from "../../api/statisticsApi";
import { cn } from "../../lib/utils";
import {
  Count, DaysPill, fmtDate, LINK, Message, OVERDUE_DAYS, Pill, ROW, Section, Segmented, StatCard, Table, TD, TH,
} from "./statsUi";

const SCOPE_OPTIONS = [
  { label: "Z otwartymi pozycjami", value: "open" },
  { label: "Wszystkie ośrodki", value: "all" },
];

const hasOpenItems = (r: ResortOverview) => r.openItems.length > 0 || r.seriousBreaches > 0;

export const ResortsTab = ({
  data, isLoading, study,
}: { data?: ResortOverview[]; isLoading: boolean; study: string }) => {
  const [scope, setScope] = useState("open");
  const [expanded, setExpanded] = useState<string | null>(null);

  const inStudy = useMemo(() => (data ?? []).filter(r => !study || r.studyName === study), [data, study]);
  const rows = scope === "open" ? inStudy.filter(hasOpenItems) : inStudy;

  if (isLoading) return <Message>Ładowanie…</Message>;
  if (!data) return <Message>Nie udało się pobrać danych</Message>;

  const sum = (f: (r: ResortOverview) => number) => inStudy.reduce((a, r) => a + f(r), 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Otwarte znaleziska MAJOR" value={sum(r => r.openMajorFindings)} tone="red"
          hint={`w ${inStudy.filter(r => r.openMajorFindings > 0).length} ośrodkach`} />
        <StatCard label={`Otwarte ponad ${OVERDUE_DAYS} dni`} value={sum(r => r.overdueOpenItems)} tone="amber"
          hint="znaleziska i akcje" />
        <StatCard label="Potencjalne odstępstwa (otwarte)" value={sum(r => r.openPotentialDeviations)} tone="amber"
          hint="akcje do oceny jako PD" />
        <StatCard label="Poważne naruszenia" value={sum(r => r.seriousBreaches)} tone="red"
          hint="akcje oznaczone jako serious breach" />
      </div>

      <Section
        title="Ośrodki wymagające uwagi"
        description="Znaleziska i akcje skopiowane do kolejnych raportów liczone są raz, w stanie z najnowszego raportu. Kliknij wiersz, aby zobaczyć otwarte pozycje."
        actions={<Segmented<string> options={SCOPE_OPTIONS} value={scope} onChange={setScope} />}
      >
        {rows.length === 0 ? <Message>Brak ośrodków z otwartymi pozycjami</Message> : (
          <Table head={<>
            <TH>Ośrodek</TH><TH right>MAJOR</TH><TH right>MINOR</TH><TH right>Akcje</TH>
            <TH right>{`> ${OVERDUE_DAYS} dni`}</TH><TH right>Pot. odstępstwa</TH><TH right>Poważne naruszenia</TH>
            <TH>Ostatnia wizyta</TH><TH>Następna wizyta</TH>
          </>}>
            {rows.map(r => {
              const open = expanded === r.resortId;
              return (
                <Fragment key={r.resortId}>
                  <tr
                    onClick={() => setExpanded(open ? null : r.resortId)}
                    className={cn(ROW, "cursor-pointer hover:bg-[var(--sc-bg)]", open && "bg-[var(--sc-bg)]")}
                  >
                    <TD>
                      <div className="font-medium">{r.resortName}</div>
                      <div className="text-[11px] text-[var(--sc-muted)]">{r.studyName}</div>
                    </TD>
                    <TD right><Count n={r.openMajorFindings} tone="red" /></TD>
                    <TD right><Count n={r.openMinorFindings} /></TD>
                    <TD right><Count n={r.openActions} /></TD>
                    <TD right><Count n={r.overdueOpenItems} tone="amber" /></TD>
                    <TD right><Count n={r.openPotentialDeviations} tone="amber" /></TD>
                    <TD right><Count n={r.seriousBreaches} tone="red" /></TD>
                    <TD className="whitespace-nowrap">{fmtDate(r.lastVisitDate)}</TD>
                    <TD className="whitespace-nowrap">
                      {r.nextPlannedVisitDate ? fmtDate(r.nextPlannedVisitDate) : <span className="text-[var(--sc-muted)]">brak w planie</span>}
                    </TD>
                  </tr>
                  {open && (
                    <tr className={ROW}>
                      <td colSpan={9} className="pb-4">
                        <OpenItems resort={r} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </Table>
        )}
      </Section>
    </div>
  );
};

const OpenItems = ({ resort }: { resort: ResortOverview }) => (
  <div className="bg-[var(--sc-bg)] rounded-lg p-4">
    <div className="flex justify-between items-center mb-2">
      <span className="text-[12px] text-[var(--sc-muted)]">
        Otwarte pozycje: {resort.openItems.length} · raportów w ośrodku: {resort.reportCount}
      </span>
      {resort.latestReportId && (
        <Link to={`/reports/${resort.latestReportId}`} className={cn(LINK, "text-[12px]")}>Ostatni raport →</Link>
      )}
    </div>
    {resort.openItems.length === 0 ? <Message>Brak otwartych pozycji</Message> : (
      <Table head={<><TH>Rodzaj</TH><TH>Kategoria</TH><TH>Opis</TH><TH>Uczestnik</TH><TH>Od</TH><TH>Otwarte</TH><TH /></>}>
        {resort.openItems.map((i, idx) => (
          <tr key={idx} className={ROW}>
            <TD className="whitespace-nowrap">
              {i.kind === "finding"
                ? <Pill tone={i.major ? "red" : "neutral"}>{i.major ? "Znalezisko MAJOR" : "Znalezisko MINOR"}</Pill>
                : <Pill tone={i.seriousBreach ? "red" : i.potentialDeviation ? "amber" : "neutral"}>
                    {i.seriousBreach ? "Akcja · naruszenie" : i.potentialDeviation ? "Akcja · pot. PD" : "Akcja"}
                  </Pill>}
            </TD>
            <TD>{i.category || "—"}</TD>
            <TD className="max-w-[420px]">{i.description || "—"}</TD>
            <TD>{i.participantNumber || "—"}</TD>
            <TD className="whitespace-nowrap">{fmtDate(i.identificationDate)}</TD>
            <TD><DaysPill days={i.daysOpen} /></TD>
            <TD><Link to={`/reports/${i.reportId}`} className={LINK}>raport</Link></TD>
          </tr>
        ))}
      </Table>
    )}
  </div>
);
